import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from './iconMap';
import './theme-picker.css';

/**
 * Sprint languages first (en/ar/hi have V3 coverage); the legacy template
 * locales remain reachable below the divider.
 *
 * NOTE: Malayalam (ml) is temporarily hidden from this selector — the i18n
 * config + backend strings remain intact, so re-add the entry below to restore.
 */
const LANGUAGES = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'ar', name: 'Arabic', native: 'العربية' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
];

/**
 * Language picker — sits in the V3 sidebar account bar (the V2 header, which
 * hosts the legacy switcher, is hidden on V3 screens). Reuses the ThemePicker
 * popover styling. Persistence is handled by i18next-browser-languagedetector
 * (localStorage); <html dir/lang> sync happens in src/i18n.jsx.
 */
export default function LanguagePicker() {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const popRef = useRef(null);

    const current = (i18n.language || 'en').split('-')[0];

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (triggerRef.current && triggerRef.current.contains(e.target)) return;
            if (popRef.current && popRef.current.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [open]);

    const apply = useCallback((code) => {
        i18n.changeLanguage(code);
        setOpen(false);
    }, [i18n]);

    return (
        <div className="v3-theme-picker">
            <button
                ref={triggerRef}
                className={`v3-account-btn${current !== 'en' ? ' is-active' : ''}`}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t('Change language')}
                data-tooltip={t('Language')}
            >
                <Globe size={16} />
            </button>

            {open && createPortal(
                <div className="v3-tp-popover" role="menu" ref={popRef}>
                    <div className="v3-tp-title">{t('Language')}</div>
                    <div className="v3-tp-list">
                        {LANGUAGES.map((lang) => {
                            const active = current === lang.code;
                            return (
                                <button
                                    key={lang.code}
                                    role="menuitemradio"
                                    aria-checked={active}
                                    className={`v3-tp-opt${active ? ' active' : ''}`}
                                    onClick={() => apply(lang.code)}
                                >
                                    <span className="v3-tp-meta">
                                        <span className="v3-tp-name">{lang.native}</span>
                                        <span className="v3-tp-sub">{lang.name}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
